import React, { useEffect, useState } from 'react';
import { useReportService } from '../../services/ReportService';
import useLoadTracker from '@dr-pam/common-components/Hooks/useLoadTracker';
import { Button, Stack, MultiSelect, Text, Divider } from '@mantine/core';
import { useForm, schemaResolver } from '@mantine/form';
import { DateInput } from '@mantine/dates';
import NotificationUtils from '@dr-pam/common-components/Utils/NotificationUtils';
import type { CouponListFragment } from '../../graphql/graphql';
import { useCouponService } from '../../services/CouponService';
import z from 'zod';

const validationSchema = z
	.object({
		couponIds: z.array(z.string()).min(1, 'At least one coupon must be selected'),
		paymentDateFrom: z.null().or(z.coerce.date()),
		paymentDateTo: z.null().or(z.coerce.date()),
	})
	.refine((data) => !data.paymentDateFrom || !data.paymentDateTo || data.paymentDateTo >= data.paymentDateFrom, {
		message: 'End date must be after start date',
		path: ['paymentDateTo'],
	});

type CouponReportForm = {
	couponIds: string[];
	paymentDateFrom: Date | null;
	paymentDateTo: Date | null;
};

export type CouponReportProps = {
	className?: string;
	onReportGenerated: (report: Blob, filenamePrefix: string) => void;
	preselectedCouponId?: string;
};

export default function CouponReport(props: CouponReportProps) {
	const { className, onReportGenerated, preselectedCouponId } = props;

	const { isLoading, addLoader, removeLoader } = useLoadTracker();
	const [coupons, setCoupons] = useState<CouponListFragment[]>([]);

	const reportService = useReportService();
	const couponService = useCouponService();

	useEffect(() => {
		const loader = addLoader();
		const request = couponService.getAll();
		request.response
			.then((coupons) => {
				setCoupons(coupons);
			})
			.catch((err) => {
				NotificationUtils.showError(err as Error, 'Failed to load coupons');
			})
			.finally(() => {
				removeLoader(loader);
			});
	}, [addLoader, removeLoader, couponService]);

	const form = useForm<CouponReportForm>({
		initialValues: {
			couponIds: preselectedCouponId ? [preselectedCouponId] : [],
			paymentDateFrom: null,
			paymentDateTo: null,
		},
		validate: schemaResolver(validationSchema),
	});

	const handleSubmit = async (values: CouponReportForm) => {
		const loader = addLoader();
		try {
			const report = await reportService.getCouponReport({
				couponIds: values.couponIds,
				paymentDateFrom: values.paymentDateFrom ?? undefined,
				paymentDateTo: values.paymentDateTo ?? undefined,
			});
			onReportGenerated(report, 'coupon_report');
		} catch (err) {
			NotificationUtils.showError(err as Error, 'Failed to generate report');
		} finally {
			removeLoader(loader);
		}
	};

	const couponData = coupons.map((c) => ({
		value: c.id,
		label: c.code,
	}));

	return (
		<div className={`ExportOptions ${className ?? ''}`}>
			<form onSubmit={form.onSubmit(handleSubmit)}>
				<Stack maw={640}>
					<Text size="sm">
						This report will generate a CSV file containing all payments where the selected coupons were
						used. The report includes payment details, user information, products purchased, and discount
						amounts.
					</Text>
					<MultiSelect
						{...form.getInputProps('couponIds')}
						data={couponData}
						label="Coupon(s)"
						description="Select one or more coupons to include in the report"
						placeholder={isLoading ? 'Loading...' : 'Select coupons'}
						disabled={isLoading}
						searchable
						withAsterisk
					/>
					<Divider />
					<Text size="sm">You may optionally specify a date range to filter by the payment date</Text>
					<DateInput
						{...form.getInputProps('paymentDateFrom')}
						label="Payment Date From"
						description="Lower date bound for when the payment was made"
						valueFormat="DD-MM-YYYY"
						popoverProps={{ withinPortal: true }}
						disabled={isLoading}
						clearable
					/>
					<DateInput
						{...form.getInputProps('paymentDateTo')}
						label="Payment Date To"
						description="Upper date bound for when the payment was made"
						valueFormat="DD-MM-YYYY"
						popoverProps={{ withinPortal: true }}
						disabled={isLoading}
						clearable
					/>
					<Divider />
					<Button type="submit" className="flex ac jc gxs" loading={isLoading}>
						<span>Generate Report (CSV)</span>
					</Button>
				</Stack>
			</form>
		</div>
	);
}
